import { isEmpty } from 'lodash-es'
import { useQuery, useQueryClient } from '@tanstack/vue-query'

import { Logger } from '@/utils/logger'
import { normalizationCart } from '@/utils/normalize/cartServiceNormalize'

import type { ProductInterface } from '@/types/graphql'

import type {
  AddItemsParams,
  RemoveBundleParams,
  UpdateBundleParams,
} from '@/types/sdk'
import type { CartServiceProps } from '@/types/cartService'
import { useSdk } from '@/composables/useSdk'
import cartGetters from '~/utils/getters/cartGetters'
import { useUserStore } from '~/store/user'
import {
  readonly,
  ref,
  useGtmTrackEvent,
  useNuxtApp,
  useState,
  watch,
  useRuntimeConfig,
  navigateTo,
  useRoute,
  computed,
} from '#imports'

import type { UseCartErrors, UseCartInterface } from './useCart'
import { EYE_BOT_PRESCRIPTION_ID } from '~/utils/constants/eyebot'
/**
 * Allows loading and manipulating cart of the current user.
 *
 * See the {@link UseCartInterface} for a list of methods and values available in this composable.
 */

let createCartPromise: Promise<any> | null = null

export function useCart<
  PRODUCT extends ProductInterface,
>(): UseCartInterface<any> {
  const loading = useState('loading', () => false)
  const cartError = ref<UseCartErrors>({
    addItem: null,
    removeItem: null,
    updateItemQty: null,
    load: null,
    clear: null,
  })
  const context = useNuxtApp()
  const apiState = context.$magento.config.state
  const { cartService } = useSdk()
  const queryClient = useQueryClient()
  const { addToCartFrameGlassesTrackEvent } = useGtmTrackEvent()
  const userStore = useUserStore()
  const config = useRuntimeConfig()
  const route = useRoute()

  const isEmployeeSite = config.public?.site === 'Employee'

  // Check if we're in a login flow
  const isLoginRoute = () => {
    return route.path.includes('/login') || route.path.includes('/amazon/login')
  }

  // Check login and redirect if not logged in - but don't redirect on login pages
  const checkLoginAndRedirect = () => {
    if (!userStore.loggedIn && !isEmployeeSite) {
      // Skip redirecting if we're on a login page
      if (isLoginRoute()) {
        return false
      }
      navigateTo('/time-out')
      return false
    }
    return true
  }

  /**
   * Assign new cart object
   * @param newCart
   *
   * @return void
   */
  const setCart = (newCart: any): void => {
    Logger.debug('useCart.setCart', newCart)
    queryClient.setQueryData(['useCart-load'], newCart)
  }

  const isInCartBySkuId = (product: PRODUCT): boolean => {
    if (!checkLoginAndRedirect()) return false
    const cartFrameItems = cartGetters.getFrameItems(cart.value as any)
    return cartFrameItems.some(
      (cartItem: any) => cartItem?.product?.sku === product.sku,
    )
  }

  const createCart = async (forceUpdate = false) => {
    // Skip cart creation if not logged in, but don't redirect
    if (!userStore.loggedIn && !isEmployeeSite) return

    const apiStateCartIds = apiState.getCartIds()
    const storeCode = userStore?.store?.storeCode ?? ''
    const storeCartId =
      !isEmpty(storeCode) && (apiStateCartIds?.[storeCode] ?? '') === ''
    if (storeCartId || !apiStateCartIds || forceUpdate) {
      const createPromise = createCartPromise || cartService.createCart()
      createCartPromise = createPromise
      const { data } = await createPromise
      const cartId = {
        ...apiStateCartIds,
        [storeCode]: data.value?.cartId,
      }
      apiState.setCartIds(JSON.stringify(cartId), { path: '/' })
      createCartPromise = null
    }
  }

  const {
    refetch: loadCart,
    data: cart,
    error: loadCartError,
  } = useQuery({
    queryKey: ['useCart-load'],
    queryFn: async () => {
      // Don't redirect here, just return null if not logged in
      if (!userStore.loggedIn && !isEmployeeSite) return null

      await createCart()
      const res = await cartService.getCart()
      return res.data.value
    },
    select: (oldCart) => {
      if (!oldCart) return null
      return normalizationCart(oldCart as unknown as CartServiceProps)
    },
    retry: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: true,
    enabled: (userStore.loggedIn && isEmployeeSite) || userStore.loggedIn,
  })

  watch(loadCartError, () => {
    cartError.value.load = loadCartError.value
  })

  const clear = async (): Promise<void> => {
    Logger.debug('useCart.clear')
    if (!checkLoginAndRedirect()) return
    try {
      loading.value = true
      await createCart(true)
    } catch (err) {
      cartError.value.clear = err
      Logger.error('useCart/clear', err)
    } finally {
      loading.value = false
    }
  }

  const addItem = async (addItemsParams: AddItemsParams): Promise<void> => {
    Logger.debug('useCart.addItem')
    if (!checkLoginAndRedirect()) return
    try {
      loading.value = true
      await createCart()
      const newCart = await cartService.addItems(addItemsParams)
      if (newCart.error.value) {
        throw newCart.error.value
      }
      queryClient.setQueryData(['useCart-load'], newCart.data.value)
      const frameSku = addItemsParams.items[0].sku
      const frameItems = cartGetters.getFrameItems(cart.value as any)
      const frameItem = frameItems.find(({ sku }: any) => sku === frameSku)
      if (frameItem) {
        addToCartFrameGlassesTrackEvent({
          sku: frameItem.sku,
          name: frameItem.product.name,
          price: frameItem.prices.row_total?.value,
        })
      }
      cartError.value.addItem = null
    } catch (err) {
      cartError.value.addItem = err
      Logger.error('useCart/addItem', err)
    } finally {
      loading.value = false
    }
  }

  const removeItem = async (params: RemoveBundleParams) => {
    Logger.debug('useCart.removeItem')
    if (!checkLoginAndRedirect()) return
    try {
      loading.value = true
      const newCart = await cartService.removeBundle(params)
      if (newCart.error.value) {
        throw newCart.error.value
      }
      queryClient.setQueryData(['useCart-load'], newCart.data.value)
      cartError.value.removeItem = null
    } catch (err) {
      cartError.value.removeItem = err
      Logger.error('useCart/removeItem', err)
    } finally {
      loading.value = false
    }
  }

  const updateItem = async (params: UpdateBundleParams): Promise<void> => {
    Logger.debug('useCart.updateItem')
    if (!checkLoginAndRedirect()) return
    try {
      loading.value = true
      const newCart = await cartService.updateBundle(params)
      if (newCart.error.value) {
        throw newCart.error.value
      }
      queryClient.setQueryData(['useCart-load'], newCart.data.value)
      cartError.value.updateItemQty = null
    } catch (err) {
      cartError.value.updateItemQty = err
      Logger.error('useCart/updateItemQty', err)
    } finally {
      loading.value = false
    }
  }

  const nonPrescriptionItemsCount = computed(() => {
    return cart.value?.items.filter(
      (item: any) => item.bundleItems[0].sku !== EYE_BOT_PRESCRIPTION_ID,
    ).length
  })

  return {
    setCart,
    cart,
    isInCartBySkuId,
    addItem,
    load: loadCart,
    removeItem,
    clear,
    updateItem,
    loading: readonly(loading),
    error: cartError,
    nonPrescriptionItemsCount,
  }
}

export default useCart
export * from './useCart'
